import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { RemoteDiagnosticsActions } from './remote-diagnostics.actions';
import {
  ActiveErrorWithDiagStackDetail,
  DIAGNOSTICS_CONNECTION_STATUS,
  DIAGNOSTICS_CONNECTION_STEP,
  DiagStackConnectionProperties,
  HydraulicSchemeParams,
  RemoteDiagnosticsControlUnit,
  RemoteDiagnosticsParameter,
  RemoteDiagnosticsTab,
  RemoteDiagnosticsVddListParameter,
  SetupInfo,
  XAuthTokenAndDiagStackNode,
  backupMainLogicalLink,
} from './remote-diagnostics.model';
import { RemoteDiagnosticsSelectors } from './remote-diagnostics.selectors';

@Injectable({ providedIn: 'root' })
export class RemoteDiagnosticsStore {
  constructor(private store: Store) {}

  vddDetailsList$ = this.store.select(RemoteDiagnosticsSelectors.vddDetailsList);
  loading$ = this.store.select(RemoteDiagnosticsSelectors.isLoading());
  isSessionStatusChecked$ = this.store.select(RemoteDiagnosticsSelectors.isSessionStatusChecked());
  activeSessionStatusChecked$ = this.store.select(
    RemoteDiagnosticsSelectors.activeSessionStatusChecked
  );
  getActiveConnectionStep$ = this.store.select(
    RemoteDiagnosticsSelectors.getActiveConnectionStep()
  );
  getActiveSessions$ = this.store.select(RemoteDiagnosticsSelectors.getActiveSessions());
  getAvailableIntervals$ = this.store.select(RemoteDiagnosticsSelectors.getIntervals());
  logicalLinkList$ = this.store.select(RemoteDiagnosticsSelectors.getLogicalLinkList());
  selectLogicalLinkOpenLoading$ = this.store.select(
    RemoteDiagnosticsSelectors.selectLogicalLinkOpenLoading
  );
  selectVddListLoading$ = this.store.select(RemoteDiagnosticsSelectors.selectVddListLoading);
  isHydraulicSchemaAvailable$ = this.store.select(
    RemoteDiagnosticsSelectors.isHydraulicSchemaAvailable()
  );
  scheduleMultipleParametersReadCycleInProgress$ = this.store.select(
    RemoteDiagnosticsSelectors.scheduleMultipleParametersReadCycleInProgress
  );
  cancelMultipleParametersReadCycleInProgress$ = this.store.select(
    RemoteDiagnosticsSelectors.cancelMultipleParametersReadCycleInProgress
  );
  selectIsChartUncollapsed$ = this.store.select(
    RemoteDiagnosticsSelectors.selectIsChartUncollapsed
  );

  getConnectionStatus(): Observable<DIAGNOSTICS_CONNECTION_STATUS> {
    return this.store.select(RemoteDiagnosticsSelectors.getConnectionStatus());
  }

  getDiagStackNode(gatewaySerial: string): Observable<string> {
    return this.store.select(RemoteDiagnosticsSelectors.getDiagStackNode(gatewaySerial));
  }

  getConnectionReason(): Observable<string> {
    return this.store.select(RemoteDiagnosticsSelectors.getConnectionReason());
  }

  getSessionGuid(gatewaySerial: string): Observable<string> {
    return this.store.select(RemoteDiagnosticsSelectors.getSessionGuid(gatewaySerial));
  }

  getDiagStackSetupInfo(gatewaySerial: string): Observable<SetupInfo> {
    return this.store.select(RemoteDiagnosticsSelectors.getDiagStackSetupInfo(gatewaySerial));
  }

  getParametersWhichAreBeingRead(gatewaySerial: string): Observable<RemoteDiagnosticsParameter[]> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getParametersWhichAreBeingRead(gatewaySerial)
    );
  }

  getAvailableParameters(gatewaySerial: string): Observable<RemoteDiagnosticsVddListParameter[]> {
    return this.store.select(RemoteDiagnosticsSelectors.getAvailableParameters(gatewaySerial));
  }

  getParametersSelectedOnly(gatewaySerial: string): Observable<RemoteDiagnosticsParameter[]> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getSelectedParametersSelectedOnly(gatewaySerial)
    );
  }

  getParametersStartedAndSelected(gatewaySerial: string): Observable<RemoteDiagnosticsParameter[]> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getSelectedParameterStartedAndSelected(gatewaySerial)
    );
  }

  getParametersStartedOrSelected(gatewaySerial: string): Observable<RemoteDiagnosticsParameter[]> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getSelectedParameterStartedOrSelected(gatewaySerial)
    );
  }

  getAvailableParametersExceptSelected(
    gatewaySerial: string
  ): Observable<RemoteDiagnosticsVddListParameter[]> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getAvailableParametersExceptSelected(gatewaySerial)
    );
  }

  isActiveSessionInBackendStorage(gatewaySerial: string): Observable<boolean> {
    return this.store.select(
      RemoteDiagnosticsSelectors.isActiveSessionInBackendStorage(gatewaySerial)
    );
  }

  getControlUnits(gatewaySerial: string): Observable<RemoteDiagnosticsControlUnit[]> {
    return this.store.select(RemoteDiagnosticsSelectors.getControlUnits(gatewaySerial));
  }

  getMainLogicalLinkFromTopology(gatewaySerial: string): Observable<string> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getMainLogicalLinkFromTopology(gatewaySerial)
    );
  }

  getBundleIdFromTopologyAndHydrauliMatrixVddDetails(
    gatewaySerial: string,
    vddName: string,
    logicalLink: string
  ): Observable<HydraulicSchemeParams> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getBundleIdFromTopologyAndHydrauliMatrixVddDetails(
        gatewaySerial,
        vddName,
        logicalLink
      )
    );
  }

  getSelectedControlUnit(gatewaySerial: string): Observable<RemoteDiagnosticsControlUnit> {
    return this.store.select(RemoteDiagnosticsSelectors.getSelectedControlUnit(gatewaySerial));
  }

  selectRemoteDiagnosticsTabs(gatewaySerial: string): Observable<RemoteDiagnosticsTab[]> {
    return this.store.select(RemoteDiagnosticsSelectors.selectRemoteDiagnosticsTabs(gatewaySerial));
  }

  getSelectedParameters(gatewaySerial: string): Observable<RemoteDiagnosticsParameter[]> {
    return this.store.select(RemoteDiagnosticsSelectors.getSelectedParameters(gatewaySerial));
  }

  getSelectedParametersStoppedAndSelectedNotInError(
    gatewaySerial: string
  ): Observable<RemoteDiagnosticsParameter[]> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getSelectedParametersStoppedAndSelectedNotInError(gatewaySerial)
    );
  }

  isAnyParameterLoading(gatewaySerial: string): Observable<boolean> {
    return this.store.select(RemoteDiagnosticsSelectors.isAnyParameterLoading(gatewaySerial));
  }

  getSelectedParametersLogicalLink(gatewaySerial: string, vddName: string): Observable<string> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getSelectedParametersLogicalLink(gatewaySerial, vddName)
    );
  }

  getXAuthToken(gatewaySerial: string): Observable<string> {
    return this.store.select(RemoteDiagnosticsSelectors.getXAuthToken(gatewaySerial));
  }

  getLastUserActivityTimestamp(gatewaySerial: string): Observable<string> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getLastUserActivityTimestamp(gatewaySerial)
    );
  }

  getTechnicianWsUrl(gatewaySerial: string): Observable<string> {
    return this.store.select(RemoteDiagnosticsSelectors.getTechnicianWsUrl(gatewaySerial));
  }

  getVciResource(gatewaySerial: string): Observable<string> {
    return this.store.select(RemoteDiagnosticsSelectors.getVciResource(gatewaySerial));
  }

  getVciName(gatewaySerial: string): Observable<string> {
    return this.store.select(RemoteDiagnosticsSelectors.getVciName(gatewaySerial));
  }

  getSelectedVinNumber(gatewaySerial: string): Observable<string> {
    return this.store.select(RemoteDiagnosticsSelectors.getVinNumber(gatewaySerial));
  }

  getConnectedDeviceSerial(gatewaySerial: string): Observable<string> {
    return this.store.select(RemoteDiagnosticsSelectors.getConnectedDeviceSerial(gatewaySerial));
  }

  getActiveLogicalLinks(gatewaySerial: string): Observable<string[]> {
    return this.store.select(RemoteDiagnosticsSelectors.getActiveLogicalLinks(gatewaySerial));
  }

  getLogicalLinkOfSelectedActiveError(gatewaySerial: string): Observable<string> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getLogicalLinkOfSelectedActiveError(gatewaySerial)
    );
  }

  getNameOfSelectedActiveError(gatewaySerial: string): Observable<string> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getNameOfSelectedActiveError(gatewaySerial)
    );
  }

  isAnyParameterRunning(gatewaySerial: string): Observable<boolean> {
    return this.store.select(RemoteDiagnosticsSelectors.isAnyParameterRunning(gatewaySerial));
  }

  isActiveSessionAvailable(gatewaySerial: string): Observable<boolean> {
    return this.store.select(RemoteDiagnosticsSelectors.isActiveSessionAvailable(gatewaySerial));
  }

  isActiveSessionAvailableBasedOnStatusFeatureOnly(): Observable<boolean> {
    return this.store.select(
      RemoteDiagnosticsSelectors.isActiveSessionAvailableBasedOnStatusFeatureOnly()
    );
  }

  isAnyParameterStoppedAndSelected(gatewaySerial: string): Observable<boolean> {
    return this.store.select(
      RemoteDiagnosticsSelectors.isAnyParameterStoppedAndSelected(gatewaySerial)
    );
  }

  isUseCasePopupBlocked$: Observable<boolean> = this.store.select(
    RemoteDiagnosticsSelectors.selectConfirmPopupState
  );

  isAnyParameterStoppedAndSelectedAndNotInError(gatewaySerial: string): Observable<boolean> {
    return this.store.select(
      RemoteDiagnosticsSelectors.isAnyParameterStoppedAndSelectedAndNotInError(gatewaySerial)
    );
  }

  isAnyParameterRunningAndSelected(gatewaySerial: string): Observable<boolean> {
    return this.store.select(
      RemoteDiagnosticsSelectors.isAnyParameterRunningAndSelected(gatewaySerial)
    );
  }

  isDiagnosticsSessionRunning(gatewaySerial: string): Observable<boolean> {
    return this.store.select(RemoteDiagnosticsSelectors.isDiagnosticsSessionRunning(gatewaySerial));
  }

  isDiagnosticsSessionInactive(gatewaySerial: string): Observable<boolean> {
    return this.store.select(
      RemoteDiagnosticsSelectors.isDiagnosticsSessionInactive(gatewaySerial)
    );
  }

  isParameterListEmpty(gatewaySerial: string): Observable<boolean> {
    return this.store.select(RemoteDiagnosticsSelectors.isParameterListEmpty(gatewaySerial));
  }

  isParameterListFull(gatewaySerial: string): Observable<boolean> {
    return this.store.select(RemoteDiagnosticsSelectors.isParameterListFull(gatewaySerial));
  }

  isAnyParameterSelected(gatewaySerial: string): Observable<boolean> {
    return this.store.select(RemoteDiagnosticsSelectors.isAnyParameterSelected(gatewaySerial));
  }

  isAnyParameterNotSelected(gatewaySerial: string): Observable<boolean> {
    return this.store.select(RemoteDiagnosticsSelectors.isAnyParameterNotSelected(gatewaySerial));
  }

  getDiagStackConnectionProperties(
    gatewaySerial: string
  ): Observable<DiagStackConnectionProperties> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getDiagStackConnectionProperties(gatewaySerial)
    );
  }

  getXAuthTokenAndDiagStackNode(gatewaySerial: string): Observable<XAuthTokenAndDiagStackNode> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getXAuthTokenAndDiagStackNode(gatewaySerial)
    );
  }

  openLogicalLink(gatewaySerial: string, logicalLinkName: string, loadVddList?: boolean): void {
    this.store.dispatch(
      RemoteDiagnosticsActions.logicalLinkOpen.attempt({
        gatewaySerial,
        logicalLinkName: logicalLinkName ?? backupMainLogicalLink, // could be extended with main topology LL checkup ( unrelated )
        loadVddList: loadVddList ?? false,
      })
    );
  }

  openLogicalLinkBeforeVddReadingAttempt(
    gatewaySerial: string,
    logicalLinkName: string,
    vddNames: string[]
  ): void {
    this.store.dispatch(
      RemoteDiagnosticsActions.openLogicalLinkBeforeVddReading.attempt({
        gatewaySerial,
        logicalLinkName: logicalLinkName ?? backupMainLogicalLink,
        vddNames,
      })
    );
  }

  findDiagStackInstance(gatewaySerial: string, deviceSerial: string): void {
    this.store.dispatch(
      RemoteDiagnosticsActions.diagstackGetFreeInstance.attempt({ gatewaySerial, deviceSerial })
    );
  }

  loadLogicalLinkList(gatewaySerial: string): void {
    this.store.dispatch(RemoteDiagnosticsActions.logicalLinkList.attempt({ gatewaySerial }));
  }

  hasRestrictedAccessToActiveDiagnosticsSession(gatewaySerial: string): Observable<boolean> {
    return this.store.select(
      RemoteDiagnosticsSelectors.hasRestrictedAccessToActiveDiagnosticsSession(gatewaySerial)
    );
  }

  setActiveConnectionStep(step: DIAGNOSTICS_CONNECTION_STEP): void {
    this.store.dispatch(RemoteDiagnosticsActions.setActiveConnectionStep({ payload: step }));
  }

  setConnectionStatus(step: DIAGNOSTICS_CONNECTION_STATUS): void {
    this.store.dispatch(RemoteDiagnosticsActions.setConnectionStatus({ payload: step }));
  }

  saveConnectionReason(connectionReason: string): void {
    this.store.dispatch(
      RemoteDiagnosticsActions.saveConnectionReason({
        connectionReason,
      })
    );
  }

  triggerDisconnectSequence(gatewaySerial: string): void {
    this.store.dispatch(RemoteDiagnosticsActions.disconnect.attempt({ gatewaySerial }));
  }

  addParameter(
    controlUnit: RemoteDiagnosticsControlUnit,
    vdd: RemoteDiagnosticsVddListParameter,
    gatewaySerial: string
  ): void {
    this.store.dispatch(
      RemoteDiagnosticsActions.parameter.add({ controlUnit, vdd, gatewaySerial })
    );
  }

  selectControlUnit(controlUnit: RemoteDiagnosticsControlUnit, gatewaySerial: string): void {
    this.store.dispatch(RemoteDiagnosticsActions.selectControlUnit({ controlUnit, gatewaySerial }));
  }

  deleteParameter(parameterId: number, gatewaySerial: string): void {
    this.store.dispatch(RemoteDiagnosticsActions.parameter.delete({ parameterId, gatewaySerial }));
  }

  selectAllParameters(gatewaySerial: string): void {
    this.store.dispatch(RemoteDiagnosticsActions.allParameters.select({ gatewaySerial }));
  }

  unselectAllParameters(gatewaySerial: string): void {
    this.store.dispatch(RemoteDiagnosticsActions.allParameters.unselect({ gatewaySerial }));
  }

  changeParameter(parameter: RemoteDiagnosticsParameter, gatewaySerial: string): void {
    this.store.dispatch(RemoteDiagnosticsActions.parameter.change({ parameter, gatewaySerial }));
  }

  changeIntervalAllParameters(interval: number, gatewaySerial: string): void {
    this.store.dispatch(
      RemoteDiagnosticsActions.allParameters.changeInterval({ interval, gatewaySerial })
    );
  }

  /** This does not stop cycle read of the parameter. Always cancel cycle read before removing! */
  deleteSelectedParameters(gatewaySerial: string): void {
    this.store.dispatch(RemoteDiagnosticsActions.deleteSelectedParameters({ gatewaySerial }));
  }

  scheduleParameterReadCycle(parameter: RemoteDiagnosticsParameter, gatewaySerial: string): void {
    this.store.dispatch(
      RemoteDiagnosticsActions.scheduleParameterReadCycle.attempt({ parameter, gatewaySerial })
    );
  }

  scheduleParametersReadCycleAttempt(gatewaySerial: string, interval: number): void {
    this.store.dispatch(
      RemoteDiagnosticsActions.scheduleMultipleParametersReadCycle.attempt({
        gatewaySerial,
        interval,
      })
    );
  }

  cancelParameterReadCycle(parameter: RemoteDiagnosticsParameter, gatewaySerial: string): void {
    this.store.dispatch(
      RemoteDiagnosticsActions.cancelParameterReadCycle.attempt({ parameter, gatewaySerial })
    );
  }

  cancelMultipleParametersReadCycle(gatewaySerial: string, onlySelected?: boolean): void {
    onlySelected = onlySelected ?? false;
    this.store.dispatch(
      RemoteDiagnosticsActions.cancelMultipleParametersReadCycle.attempt({
        gatewaySerial,
        onlySelected,
      })
    );
  }

  registerVci(gatewaySerial: string, technicianWsUrl: string): void {
    this.store.dispatch(
      RemoteDiagnosticsActions.remoteVciRegister.attempt({ gatewaySerial, technicianWsUrl })
    );
  }

  getSelectedParameterIsWriteFlag(
    gatewaySerial: string,
    vddName: string,
    logicalLink: string
  ): Observable<boolean> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getSelectedParameterIsWriteFlag(
        gatewaySerial,
        vddName,
        logicalLink
      )
    );
  }

  getSelectedParameterIsReadFlag(
    gatewaySerial: string,
    vddName: string,
    logicalLink: string
  ): Observable<boolean> {
    return this.store.select(
      RemoteDiagnosticsSelectors.getSelectedParameterIsReadFlag(gatewaySerial, vddName, logicalLink)
    );
  }

  lastUserActivityTimestampReset(gatewaySerial: string): void {
    this.store.dispatch(RemoteDiagnosticsActions.lastUserActivityTimestampReset({ gatewaySerial }));
  }

  getBusDetailsFromTopology(gatewaySerial: string): Observable<ActiveErrorWithDiagStackDetail[]> {
    return this.store.select(RemoteDiagnosticsSelectors.getBusDetailsFromTopology(gatewaySerial));
  }

  tabChanged(): void {
    this.store.dispatch(RemoteDiagnosticsActions.tabChanged());
  }

  saveConfirmPopupFlag(): void {
    this.store.dispatch(RemoteDiagnosticsActions.saveConfirmPopupFlag.attempt());
  }

  getConfirmPopupFlag(): void {
    this.store.dispatch(RemoteDiagnosticsActions.getConfirmPopupFlag.attempt());
  }

  isParameterRunning(gatewaySerial: string, parameterId: number): Observable<boolean> {
    return this.store.select(
      RemoteDiagnosticsSelectors.isParameterRunning(gatewaySerial, parameterId)
    );
  }

  hideStickyChart(): void {
    this.store.dispatch(RemoteDiagnosticsActions.manageChartCollapsibility.hide());
  }
  showStickyChart(): void {
    this.store.dispatch(RemoteDiagnosticsActions.manageChartCollapsibility.show());
  }
  toggleStickyChartVisibility(): void {
    this.store.dispatch(RemoteDiagnosticsActions.manageChartCollapsibility.toggle());
  }
}
